<template>
  <div class="content">
    <h1 class="has-text-centered">Router</h1>
    <div class="box">
      <h2>Was ist ein Router?</h2>
      <ul>
        <li>Netzwerkgerät welches Netzwerkpakete zwischen mehreren Rechnernetzen weiterleitet</li>
        <li>Können Firewall-Funktionen durch Regelwerk abbilden</li>
        <li>
          Arbeitet auf der dritten Schicht (
          <i>Vermittlungsschicht/Network Layer</i>
          ) des OSI-Referenzmodells
        </li>
        <li>Befindet sich häufig an den Außengrenzen eines Netzwerks</li>
      </ul>
      <h2>Wozu benötigt man einen Router?</h2>
      <ul>
        <li>Zur Verbindung mehrerer Netzwerke mit unterschiedlichen Protokollen und Architekturen</li>
      </ul>
      <h2>Wie ist ein Router aufgebaut?</h2>
      <ul>
        <li>
          Hat in der Regel
          <b>zwei Seiten für Anschlüsse</b>
          :
        </li>
        <ul>
          <li>
            Eine ist die
            <b>LAN</b>
            -Seite ("
            <i>
              <u><b>L</b></u>
              ocal
              <b><u>A</u></b>
              rea
              <u><b>N</b></u>
              etwork
            </i>
            " - lokales Netzwerk)
          </li>
          <li>
            Und eine ist die
            <b>WAN</b>
            -Seite (
            <i>
              "
              <b><u>W</u></b>
              ide
              <b><u>A</u></b>
              rea
              <b><u>N</u></b>
              etwork"
            </i>
            - öffentliches Netzwerk)
          </li>
        </ul>
      </ul>
      <h2>Was macht ein Router?</h2>
      <ul>
        <li>
          Die Aufgabe eines Routers lässt sich ein 4 Schritte einteilen und wird auch &#8220;
          <i>Routing</i>
          &#8221; genannt:
        </li>
        <ul>
          <li>Ermittlung der verfügbaren Routen</li>
          <li>Auswahl der geeignetsten Route unter Berücksichtigung verschiedener Kriterien</li>
          <li>Herstellen einer physikalischen Verbindung zu anderen Netzwerken</li>
          <li>
            Anpassen der Datenpakete an die Übertragungstechnik (&#8220;
            <i>Fragmentierung</i>
            &#8221;)
          </li>
        </ul>
      </ul>
      <h2>Was ist Routing?</h2>
      <ul>
        <li>Routing ist die Wegfindung für ein Datenpaket in einem Netzwerk</li>
        <li>
          &#8222;
          <i>Routing-Tabelle</i>
          &#8220; dient als Hilfsmittel zur Wegfindung
        </li>
        <li>
          Weg wird anhand von Kriterien (&#8222;
          <i>metric</i>
          &#8220;) ermittelt
        </li>
        <li>Je mehr Kriterien ..</li>
        <ul>
          <li>.. desto optimaler wird der Weg gewählt</li>
          <li>.. desto (zeit-)aufwendiger ist die Bestimmung oder Berechnung des Wegs</li>
        </ul>
      </ul>
      <ul>
        <ul></ul>
      </ul>
      <h3>Die Routingtabelle</h3>
      <ul>
        <li>In ihrer Funktion mit einem Adressbuch vergleichbar</li>
        <li>Kann man dynamisch oder manuell verwalten</li>
        <li>Beim dynamischen Routing verwalten Geräte ihre Routingtabellen automatisch</li>
      </ul>
      <h4>Aufbau einer Routingtabelle</h4>
      <table>
        <tbody>
          <tr>
            <th>Netzwerkadresse</th>
            <th>Subnetzmaske</th>
            <th>Next hop</th>
            <th>Metrik (optional)</th>
          </tr>
          <tr>
            <td>......</td>
            <td>......</td>
            <td>......</td>
            <td>......</td>
          </tr>
          <tr>
            <td>......</td>
            <td>......</td>
            <td>......</td>
            <td>......</td>
          </tr>
        </tbody>
      </table>
      <ul>
        <li>
          <b>Netzadresse</b>
          und
          <b>Subnetzmaske</b>
          <br />
          Diese beiden Angaben bestimmten den IP-Adresspräfix eines Zielnetzes. Daher beide zusammen identifizieren das Ziel, auf welches sich dieser Tabelleneintrag bezieht
        </li>
      </ul>
      <ul>
        <li>
          <b>&#8220;next hop&#8221;&nbsp;</b>
          (
          <i>Gateway</i>
          <b>)</b>
          <br />
          Hierbei handelt es sich um die nächste Routeradresse, an die dieses Paket gesendet werden muss. In der Regel ist dies auf IP-Ebene der Nachbar-Host, an welchen das Paket weitergeleitet
          werden muss
        </li>
      </ul>
      <ul>
        <li>
          <b>Metrik</b>
          <br />
          Fast immer kommt zu jedem Eintrag auch eine Metrikinformation hinzu, die eine Information über die Erreichbarkeit eines Netzes bzw. die Weiterleitungskosten enthält. Die Metrik spielt in der
          Regel jedoch nur eine Rolle, wenn mehrere Routen für das gleiche Ziel existieren; in diesem Fall wird basierend auf der Metrik die beste Route ausgewählt.
        </li>
      </ul>
      <h4>Beispiel einer Routingtabelle</h4>
      <ul>
        <li>
          Gegeben ist ein
          <b>LAN</b>
          ("
          <i>
            <u><b>L</b></u>
            ocal&nbsp;
            <b><u>A</u></b>
            rea&nbsp;
            <u><b>N</b></u>
            etwork
          </i>
          " - lokales Netzwerk) mit drei Subnetzen und einem Zugang ins Internet
        </li>
        <li>Um die Netze miteinander zu verbinden werden drei Router eingesetzt</li>
      </ul>
      <table align="center" cellpadding="0" cellspacing="0" class="tr-caption-container noborder" style="margin-left: auto; margin-right: auto; text-align: center">
        <tbody>
          <tr>
            <td style="text-align: center" class="noborder">
              <img src="https://docs.google.com/drawings/d/18hPVE7fOWqEkgoV57mtLilQKCL9EpjuocRb8cZpAxeI/pub?w=306&amp;h=343" style="margin-left: auto; margin-right: auto" />
            </td>
          </tr>
          <tr><td class="tr-caption noborder" style="text-align: center">Dieses einfache Netzwerk dient als Beispiel</td></tr>
        </tbody>
      </table>
      <table>
        <tbody>
          <tr>
            <th colspan="5">Routingtabelle von Router 2</th>
          </tr>
          <tr>
            <th>Netzwerkadresse</th>
            <th>Subnetzmaske</th>
            <th>Gateway</th>
            <th>Schnittstelle</th>
            <th>Metrik (Anzahl der Hops)</th>
          </tr>
          <tr>
            <td>192.168.0.0</td>
            <td>255.255.255.0</td>
            <td>192.168.1.1</td>
            <td>192.168.1.2</td>
            <td>2</td>
          </tr>
          <tr>
            <td>192.168.1.0</td>
            <td>255.255.255.0</td>
            <td>192.168.1.2</td>
            <td>192.168.1.2</td>
            <td>1</td>
          </tr>
          <tr>
            <td>192.168.2.0</td>
            <td>255.255.255.0</td>
            <td>192.168.2.1</td>
            <td>192.168.2.1</td>
            <td>1</td>
          </tr>
          <tr>
            <td>0.0.0.0</td>
            <td>0.0.0.0</td>
            <td>192.168.1.1</td>
            <td>192.168.1.2</td>
            <td>3</td>
          </tr>
        </tbody>
      </table>
      <h4>Wie werden Routingtabellen angepasst?</h4>
      <ul>
        <li>
          <b>Direkt verbundene Netze</b>
          <br />
          Sie werden automatisch in eine Routingtabelle übernommen, wenn ein Interface mit eine IPAdresse konfiguriert wird.
        </li>
      </ul>
      <ul>
        <li>
          <b>Statische Routen</b>
          <br />
          Diese Wege werden durch einen Administrator eingetragen. Die Skalierbarkeit ist für diese Methode ein limitierender Faktor.
        </li>
      </ul>
      <ul>
        <li>
          <b>Dynamische Routen</b>
          <br />
          In diesem Fall lernen Router erreichbare Netze durch ein Routingprotokoll, das Informationen über das Netzwerk und seine Teilnehmer sammelt und an die Mitglieder verteilt.
        </li>
      </ul>
      <ul></ul>
      <h2>Welche Typen (Bauformen) von Routern gibt es?</h2>
      <h3>Hardware-Router</h3>
      <ul>
        <li>Hohe Geschwindigkeit</li>
        <li>Für den Dauerbetrieb ausgelegt (Verfügbarkeit von 99,999 % oder höher)</li>
        <li>Spezialisiert auf das Weiterleiten von Paketen</li>
        <li>Mehrere Terabit pro Sekunde möglich</li>
      </ul>
      <h4>Vorteile</h4>
      <ul>
        <li>Meistens einfach zu Konfigurieren</li>
        <li>Geringer Stromverbrauch</li>
        <li>Weniger Wartungsarbeiten (meist weniger Updates notwendig)</li>
      </ul>
      <h4>Nachteil</h4>
      <ul>
        <li>Nicht so flexibel wie Software-Lösung</li>
      </ul>
      <ul></ul>
      <h3>Software-Router</h3>
      <ul>
        <li>Mit gewöhnlichen Workstations oder Servern realisierbar</li>
        <li>Routing wird über Software realisiert</li>
      </ul>
      <h4>Vorteile</h4>
      <ul>
        <li>Hohe Flexibilität</li>
        <li>Funktionsvielfalt (optional Web-, FTP-, &#8230; Server möglich)</li>
      </ul>
      <h4>Nachteile</h4>
      <ul>
        <li>Höherer Stromverbrauch</li>
        <li>Meistens schwerer zu konfigurieren</li>
        <li>Oft größer als Hardware-Router</li>
        <li>Langsamer</li>
        <li>Sicherheit (Software bietet eventuell Angriffsfläche)</li>
      </ul>
      <h2>Quellen</h2>
      <ul>
        <li>
          Elektronik Kompendium (
          <i>Stand 8. August 2017</i>
          )
        </li>
        <ul>
          <li><a href="https://www.elektronik-kompendium.de/sites/net/1404181.htm">https://www.elektronik-kompendium.de/sites/net/1404181.htm</a></li>
        </ul>
      </ul>
      <ul>
        <li>
          Wikipedia (
          <i>Stand 8. August 2017</i>
          )
        </li>
        <ul>
          <li><a href="https://de.wikipedia.org/wiki/Router">https://de.wikipedia.org/wiki/Router</a></li>
        </ul>
      </ul>
    </div>
    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'

export default {
  name: 'theory-router',
  components: {
    FeedbackBox
  },
  metaInfo: {
    title: 'Router | Elektrotechnik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Lorem Ipsum'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  }
}
</script>
